// src/pages/PrivacyPage.js
import React from 'react';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom'; // Import Link for internal routing
import './PrivacyPage.css'; // Specific styles for this page
import '../index.css';       // General styles

const PrivacyPage = () => {
  // Function to get the current date for "Last updated"
  const getFormattedDate = () => {
    const date = new Date();
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  return (
    <>
      <main className="privacyPageContainer">
        <section className="privacyHero">
          {/* H1 Title */}
          <h1 className="privacyTitle">
            Privacy Policy
          </h1>
          {/* Last Updated Date */}
          <p className="privacyLastUpdated">
            Last updated: {getFormattedDate()}
          </p>
        </section>

        {/* Section 1: Who we are */}
        <section className="privacySection">
          <h2>1. Who we are</h2>
          <p>
            Nexed (“we”, “our”, “us”) provides research, insight, and intelligence services for business education brands and corporate learning organizations.
          </p>
          <h3>Controller &amp; contact address</h3>
          <p>
            Blue Ibex Ltd / Nexed Insight <br></br>
            39 Leigh Road <br></br>
            Cobham, Surrey KT11 2LF <br></br>
            Email: <a href="mailto:privacy@nexed.org">privacy@nexed.org</a>
          </p>
          <p>
            This Policy explains how we collect, use, share, and safeguard personal information obtained through nexed.org (the “Site”) and associated services (newsletters, gated downloads, webinars, dashboards, etc.).
          </p>
        </section>

        {/* Section 2: Scope of this Policy */}
        <section className="privacySection">
          <h2>2. Scope of this Policy</h2>
          <p>
            It applies to all Site visitors, newsletter subscribers, report downloaders, webinar registrants, and tool users worldwide, regardless of device or location.
          </p>
        </section>

        {/* Section 3: What information we collect - Table */}
        <section className="privacySection">
          <h2>3. What information we collect</h2>
          <div className="tableWrapper">
            <table className="privacyTable">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Typical data points</th>
                  <th>How obtained</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Contact data</td>
                  <td>Name, business email, phone, job title, organisation</td>
                  <td>Forms you submit (e.g., “Download report”, “Contact us”)</td>
                </tr>
                <tr>
                  <td>Account credentials</td>
                  <td>Strapi user ID, encrypted password</td>
                  <td>When you create an account for gated dashboards</td>
                </tr>
                <tr>
                  <td>Marketing preferences</td>
                  <td>Newsletter opt-in status, topics of interest</td>
                  <td>Cookie banner, email footer settings</td>
                </tr>
                <tr>
                  <td>Payment/billing data</td>
                  <td>Billing address, limited card details (tokenised)</td>
                  <td>Purchases of premium content (processed by Stripe)</td>
                </tr>
                <tr>
                  <td>Usage data</td>
                  <td>IP address, device &amp; browser type, referring pages, clicks, session length</td>
                  <td>Cookies, server logs, analytics (Google Analytics, Plausible)</td>
                </tr>
                <tr>
                  <td>Generated data</td>
                  <td>AI-generated survey results or dashboards you create</td>
                  <td>Created by you during a session</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            We do not intentionally collect special-category data (e.g., health information) and never knowingly collect data from children under 16.
          </p>
        </section>

        {/* Section 4: Why we use your information & legal bases - Table */}
        <section className="privacySection">
          <h2>4. Why we use your information &amp; legal bases</h2>
          <div className="tableWrapper">
            <table className="privacyTable">
              <thead>
                <tr>
                  <th>Purpose</th>
                  <th>Legal basis under UK GDPR / EU GDPR (Art 6)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Operate, secure, and debug the Site</td>
                  <td>Legitimate interests – running a functional, secure service</td>
                </tr>
                <tr>
                  <td>Deliver content you request (downloads, webinars)</td>
                  <td>Contract – fulfilling our agreement with you</td>
                </tr>
                <tr>
                  <td>Process payments for paid content</td>
                  <td>Contract</td>
                </tr>
                <tr>
                  <td>Send newsletters, event invitations, research updates</td>
                  <td>Consent (you can withdraw at any time)</td>
                </tr>
                <tr>
                  <td>Measure Site traffic, improve UX (analytics cookies)</td>
                  <td>Consent</td>
                </tr>
                <tr>
                  <td>Show tailored ads on LinkedIn / Google</td>
                  <td>Consent</td>
                </tr>
                <tr>
                  <td>Keep business records, comply with tax &amp; data-privacy laws</td>
                  <td>Legal obligation</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            We also follow the UK Data Protection Act 2018, EU GDPR, PIPEDA (Canada), and CCPA/CPRA (California) as applicable.
          </p>
        </section>

        {/* Section 5: Who we share information with - Table */}
        <section className="privacySection">
          <h2>5. Who we share information with</h2>
          <div className="tableWrapper">
            <table className="privacyTable">
              <thead>
                <tr>
                  <th>Recipient</th>
                  <th>Purpose</th>
                  <th>Location &amp; safeguards</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Hosting (Vercel / AWS – EU regions)</td>
                  <td>Serve the Site, databases, backups</td>
                  <td>European Union; infrastructure protected by ISO 27001 controls</td>
                </tr>
                <tr>
                  <td>Email &amp; CRM (Brevo, Mailchimp)</td>
                  <td>Send newsletters, manage leads</td>
                  <td>EU &amp; US; Standard Contractual Clauses (SCCs)</td>
                </tr>
                <tr>
                  <td>Payment processor (Stripe, Paddle)</td>
                  <td>Secure payment handling</td>
                  <td>EU / US; PCI-DSS compliant, SCCs</td>
                </tr>
                <tr>
                  <td>Analytics (Google Analytics, Plausible)</td>
                  <td>Website metrics</td>
                  <td>EU / US; IP anonymisation, SCCs</td>
                </tr>
                <tr>
                  <td>Advertising networks (LinkedIn, Google Ads)</td>
                  <td>Remarketing (only if you consent)</td>
                  <td>US; SCCs, opt-out links</td>
                </tr>
                <tr>
                  <td>Professional advisors &amp; regulators</td>
                  <td>Legal, accounting, compliance</td>
                  <td>UK / EU as required</td>
                </tr>
                <tr>
                  <td>Law-enforcement or courts</td>
                  <td>Compliance with legal obligations</td>
                  <td>Only where legally compelled</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            We do not sell personal data.
          </p>
        </section>

        {/* Section 6: International transfers */}
        <section className="privacySection">
          <h2>6. International transfers</h2>
          <p>Our primary servers are in the European Union. When service partners process data outside the UK/EU (e.g., in the United States), we rely on one or more of:</p>
          <ul>
            <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
            <li>The UK International Data-Transfer Addendum</li>
            <li>Partner certification under the EU–US or UK–US Data Privacy Framework</li>
            <li>Your explicit consent where necessary</li>
          </ul>
        </section>

        {/* Section 7: Data retention - Table */}
        <section className="privacySection">
          <h2>7. Data retention</h2>
          <div className="tableWrapper">
            <table className="privacyTable">
              <thead>
                <tr>
                  <th>Data type</th>
                  <th>Retention period</th>
                  <th>Action after expiry</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Contact &amp; account details</td>
                  <td>36 months after last interaction</td>
                  <td>Deleted or anonymised</td>
                </tr>
                <tr>
                  <td>Billing records</td>
                  <td>7 years (tax law)</td>
                  <td>Deleted</td>
                </tr>
                <tr>
                  <td>Analytics logs</td>
                  <td>14 months (Google default)</td>
                  <td>Deleted</td>
                </tr>
                <tr>
                  <td>AI dashboards / generated content</td>
                  <td>Until you delete it or 24 months of inactivity</td>
                  <td>Deleted</td>
                </tr>
                <tr>
                  <td>Support tickets</td>
                  <td>24 months</td>
                  <td>Deleted</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 8: Security measures */}
        <section className="privacySection">
          <h2>8. Security measures</h2>
          <ul>
            <li>TLS 1.3 encryption in transit</li>
            <li>AES-256 encryption at rest for databases &amp; backups</li>
            <li>Role-based access control in Strapi CMS</li>
            <li>Multi-factor authentication for all admin accounts</li>
            <li>Continuous vulnerability monitoring &amp; annual penetration testing</li>
          </ul>
          <p>
            No system is perfectly secure; please report any suspected breach immediately.
          </p>
        </section>

        {/* Section 9: Your rights - Table */}
        <section className="privacySection">
          <h2>9. Your rights</h2>
          <div className="tableWrapper">
            <table className="privacyTable">
              <thead>
                <tr>
                  <th>Region</th>
                  <th>You can…</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>UK / EU (GDPR)</td>
                  <td>Access, correct, delete, restrict, object, port data, withdraw consent, lodge a complaint with a supervisory authority</td>
                </tr>
                <tr>
                  <td>Canada (PIPEDA)</td>
                  <td>Access, correct, withdraw consent, complain to the Office of the Privacy Commissioner of Canada</td>
                </tr>
                <tr>
                  <td>California (CCPA/CPRA)</td>
                  <td>Know, correct, delete, port data, opt-out of “sale”/“sharing”, limit use of sensitive data, no retaliation</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            To exercise any right, email <a href="mailto:privacy@nexed.org">privacy@nexed.org</a>. We will verify your identity and respond within one month (45 days for CCPA).
          </p>
        </section>

        {/* Section 10: Automated decision-making */}
        <section className="privacySection">
          <h2>10. Automated decision-making</h2>
          <p>
            We do not conduct automated decision-making that produces legal or similarly significant effects (GDPR Art 22).
          </p>
        </section>

        {/* Section 11: Cookies */}
        <section className="privacySection">
          <h2>11. Cookies</h2>
          <p>
            See our separate <Link to="/cookies">Cookie Policy</Link> for details on cookies and other trackers, plus instructions on how to manage them.
          </p>
        </section>

        {/* Section 12: Third-party links */}
        <section className="privacySection">
          <h2>12. Third-party links</h2>
          <p>
            The Site may link to external sites (e.g., university partners, YouTube). We are not responsible for their privacy practices; review their policies before sharing data.
          </p>
        </section>

        {/* Section 13: Children’s privacy */}
        <section className="privacySection">
          <h2>13. Children’s privacy</h2>
          <p>
            The Site targets professionals and is not intended for children under 16. If we learn we hold such data, we will delete it promptly.
          </p>
        </section>

        {/* Section 14: Changes to this Policy */}
        <section className="privacySection">
          <h2>14. Changes to this Policy</h2>
          <p>
            We may revise this Policy to reflect changes in law, technology, or our services. Material updates will be announced on the Site and, where appropriate, by email. The “Last updated” date above indicates the current version.
          </p>
        </section>

        {/* Section 15: Contact & complaints */}
        <section className="privacySection">
          <h2>15. Contact &amp; complaints</h2>
          <h3>Data Protection Officer (DPO)</h3>
          <p>
            Nexed<br />
            105 Farringdon Road<br />
            London EC1R 3BU, United Kingdom<br />
            Email: <a href="mailto:privacy@nexed.org">privacy@nexed.org</a>
          </p>
          <p>If you are unsatisfied with our response you may complain to:</p>
          <ul>
            <li>UK – Information Commissioner’s Office (ICO)</li>
            <li>EU / EEA – Your local supervisory authority</li>
            <li>California – California Privacy Protection Agency (CPPA)</li>
          </ul>
        </section>

      </main>

      {/* Footer at the bottom */}
      <Footer />
    </>
  );
};

export default PrivacyPage;
